import { useEffect, useMemo, useState } from "react";
import { Modal } from "../../ui/modal";
import {
  CryptoWallet,
  CryptoToken,
  CryptoNetworkCode,
  CRYPTO_NETWORKS,
  getAddressForNetwork,
} from "../types";
import { TokenIcon, NetworkIcon } from "../CryptoIcons";
import { AlertTriangle, Copy, Share2, CheckCircle2, ChevronLeft } from "lucide-react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  wallet: CryptoWallet | null;
  token: CryptoToken;
}

const FundCryptoModal = ({ isOpen, onClose, wallet, token }: Props) => {
  const [selectedNetwork, setSelectedNetwork] = useState<CryptoNetworkCode | null>(null);
  const [copiedField, setCopiedField] = useState<"address" | null>(null);

  // Only networks where this specific token is flagged available
  const availableNetworks = useMemo(() => {
    if (!wallet) return [];
    return CRYPTO_NETWORKS.filter((n) =>
      wallet.balances.some((b) => b.network === n.code && b.token === token && b.available)
    );
  }, [wallet, token]);

  /* ---------------- Reset to network picker whenever the modal opens/closes ---------------- */
  useEffect(() => {
    if (!isOpen) {
      setSelectedNetwork(null);
      setCopiedField(null);
    }
  }, [isOpen]);

  if (!wallet) return null;

  const address = selectedNetwork ? getAddressForNetwork(wallet, selectedNetwork) : null;

  const networkInfo = selectedNetwork
    ? CRYPTO_NETWORKS.find((n) => n.code === selectedNetwork)
    : null;

  const qrCodeUrl = address
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
        address
      )}`
    : null;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField("address");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleShare = async () => {
    if (!address || !networkInfo) return;
    const text = `Token: ${token}\nNetwork: ${networkInfo.name}\nAddress: ${address}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${token} deposit address`, text });
      } catch {
        // user cancelled share sheet
      }
    } else {
      copyToClipboard(address);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-md rounded-2xl border border-gray-200 bg-white p-6
        dark:border-gray-800 dark:bg-gray-900"
    >
      {/* ── STEP 1: choose network ─────────────────────────────────────── */}
      {!selectedNetwork && (
        <>
          <div className="mb-1 flex items-center gap-2">
            <TokenIcon token={token} size={24} />
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Fund {token}
            </h2>
          </div>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            Choose the network you want to deposit {token} on
          </p>

          {availableNetworks.length === 0 ? (
            <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-400">
              {token} deposits aren't currently available on any network.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {availableNetworks.map((network) => (
                <button
                  key={network.code}
                  onClick={() => setSelectedNetwork(network.code)}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4
                    text-left transition-all hover:shadow-md hover:border-primary/20
                    dark:border-gray-800 dark:bg-gray-800 dark:hover:border-primary/30"
                >
                  <NetworkIcon network={network.code} size={28} />
                  <span className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {network.name}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-3
                text-sm text-gray-700 hover:bg-gray-100
                dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </>
      )}

      {/* ── STEP 2: show address for the selected network ─────────────── */}
      {selectedNetwork && address && networkInfo && (
        <>
          <button
            onClick={() => setSelectedNetwork(null)}
            className="mb-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ChevronLeft className="h-4 w-4" />
            Change network
          </button>

          <div className="mb-1 flex items-center gap-2">
            <TokenIcon token={token} size={22} />
            <NetworkIcon network={selectedNetwork} size={22} />
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Fund {token} via {networkInfo.name}
            </h2>
          </div>
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            Send {token} to this address on the {networkInfo.name} network
          </p>

          {/* QR code */}
          <div className="mb-3 flex justify-center">
            <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700">
              <img src={qrCodeUrl!} alt="Deposit address QR code" className="h-35 w-35" />
            </div>
          </div>

          {/* Token + Network (read-only lines) */}
          <div className="mb-1 space-y-1">
            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-800 dark:bg-gray-800">
              <span className="text-sm text-gray-600 dark:text-gray-400">Token</span>
              <span className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-white">
                <TokenIcon token={token} size={16} />
                {token}
              </span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-800 dark:bg-gray-800">
              <span className="text-sm text-gray-600 dark:text-gray-400">Network</span>
              <span className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-white">
                <NetworkIcon network={selectedNetwork} size={16} />
                {networkInfo.name}
              </span>
            </div>
          </div>

          {/* Address */}
          <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 py-2 px-4 dark:border-gray-800 dark:bg-gray-800">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Address</div>
              <button
                onClick={() => copyToClipboard(address)}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark transition-colors text-gray-800 dark:text-gray-50"
              >
                {copiedField === "address" ? (
                  <CheckCircle2 className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
                {copiedField === "address" ? "Copied!" : "Copy"}
              </button>
            </div>
            <div className="font-mono text-sm text-gray-600 dark:text-gray-400 break-all">
              {address}
            </div>
          </div>

          {/* Warning message */}
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p className="text-xs">
              Only send {token} via the {networkInfo.name} network to this address. Sending any
              other asset, or using a different network, may result in the permanent loss of your
              funds.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-3
                text-sm text-gray-700 hover:bg-gray-100
                dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
            >
              Done
            </button>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3
                text-sm text-gray-700 hover:bg-gray-100
                dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
            >
              <Share2 className="h-4 w-4" />
              Share
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default FundCryptoModal;